import { DEFAULT_STYLE, DEFAULT_LABELS } from './layer-style';

/**
 * "Save as default for this layer type": per geometry type ('point' | 'line' | 'polygon')
 * default look kept in localStorage (`ultimate-gis:style-defaults:v1`, migrated in
 * lib/stateMigrations.js). Only visual settings are stored: never the classification
 * attribute, the computed `colorExpr` or the label field (those belong to one layer).
 */
export const STYLE_DEFAULTS_KEY = 'ultimate-gis:style-defaults:v1';
export const STYLE_DEFAULTS_VERSION = 1;
export const LAYER_TYPES = ['point', 'line', 'polygon'];

const VISUAL_KEYS = ['fillColor', 'strokeColor', 'strokeWidth', 'opacity', 'pointSize', 'symbol', 'ramp', 'reversed', 'method', 'nClasses'];
const LABEL_KEYS = ['size', 'color', 'haloColor', 'haloWidth', 'placement', 'minZoom', 'allowOverlap', 'decimals', 'suffix'];

/** 'point' | 'line' | 'polygon' for a layer's features (the most common geometry); null if none. */
export function layerGeomType(features) {
  const counts = { point: 0, line: 0, polygon: 0 };
  (features || []).slice(0, 500).forEach((f) => {
    const t = f?.geometry?.type || '';
    if (t.endsWith('Point')) counts.point++;
    else if (t.endsWith('LineString')) counts.line++;
    else if (t.endsWith('Polygon')) counts.polygon++;
  });
  const best = Object.entries(counts).sort((a, b) => b[1] - a[1])[0];
  return best && best[1] > 0 ? best[0] : null;
}

/** The storable subset of a style: visual keys only; classified modes fall back to 'single'. */
export function sanitizeDefault(style) {
  const out = {};
  VISUAL_KEYS.forEach((k) => { if (style?.[k] !== undefined) out[k] = style[k]; });
  out.mode = style?.mode === 'heatmap' ? 'heatmap' : 'single';
  if (style?.labels) {
    const labels = {};
    LABEL_KEYS.forEach((k) => { if (style.labels[k] !== undefined) labels[k] = style.labels[k]; });
    out.labels = labels;
  }
  return out;
}

/** True when a parsed stored value has the current shape. */
export function isDefaultsShapeValid(v) {
  return !!v && typeof v === 'object' && v.version === STYLE_DEFAULTS_VERSION
    && !!v.types && typeof v.types === 'object' && !Array.isArray(v.types)
    && Object.values(v.types).every((s) => s && typeof s === 'object' && !Array.isArray(s));
}

function store(storage) {
  if (storage) return storage;
  try { return window.localStorage; } catch { return null; }
}

/** All saved defaults, { [layerType]: partialStyle }; {} when absent or unreadable. */
export function loadStyleDefaults(storage) {
  const s = store(storage);
  if (!s) return {};
  try {
    const parsed = JSON.parse(s.getItem(STYLE_DEFAULTS_KEY));
    return isDefaultsShapeValid(parsed) ? parsed.types : {};
  } catch { return {}; }
}

/** Save `style` as the default for `layerType`; returns false for an unknown type or no storage. */
export function saveStyleDefault(layerType, style, storage) {
  const s = store(storage);
  if (!s || !LAYER_TYPES.includes(layerType)) return false;
  const types = { ...loadStyleDefaults(s), [layerType]: sanitizeDefault(style) };
  try {
    s.setItem(STYLE_DEFAULTS_KEY, JSON.stringify({ version: STYLE_DEFAULTS_VERSION, types }));
    return true;
  } catch { return false; }
}

export function clearStyleDefault(layerType, storage) {
  const s = store(storage);
  if (!s) return false;
  const types = { ...loadStyleDefaults(s) };
  delete types[layerType];
  try {
    s.setItem(STYLE_DEFAULTS_KEY, JSON.stringify({ version: STYLE_DEFAULTS_VERSION, types }));
    return true;
  } catch { return false; }
}

/**
 * Starting draft for a layer: app defaults < saved per-type default < the layer's own style.
 * Labels merge the same way but only exist once something sets them.
 */
export function initialStyle(layerType, layerStyle, defaults = loadStyleDefaults()) {
  const typed = (layerType && defaults[layerType]) || {};
  const merged = { ...DEFAULT_STYLE, ...typed, ...(layerStyle || {}) };
  if (typed.labels || layerStyle?.labels) merged.labels = { ...DEFAULT_LABELS, ...(typed.labels || {}), ...(layerStyle?.labels || {}) };
  return merged;
}
